import { ref } from 'vue';
import { defineStore } from 'pinia';
import ApiService from '@/core/services/ApiService';
import JwtService from '@/core/services/JwtService';
import BaseHandlerService from '@/core/services/BaseHandlerService';
import type BaseResponse from '@/core/models/Base/BaseResponse';
import type LoginResponse from '@/core/models/Authentication/LoginResponse';
import {
	ID_USER_EMAIL_KEY,
	ID_USER_NAME_KEY,
	ID_ROLE_NAME_KEY,
} from '@/core/constants/LSKeys';

export interface User {
	id: number;
	name: string;
	surname: string;
	email: string;
	password: string;
	api_token: string;
	api_refresh_token: string;
	reCaptchaToken: string;
}

export interface twoFactorRequest {
	email: string;
	password: string;
	twoFactorCode: number;
}

export interface SharedLoginProcessRequest {
	userEmail: string;
	userName: string;
	roleName: string;
}

export const useAuthStore = defineStore('auth', () => {
	const errors = ref('');
	const user = ref<User>({} as User);
	const isAuthenticated = ref(!!JwtService.getToken());
	const isTwoFactorEnabled = ref(false);
	const isFirstLogin = ref(false);
	const barCodeToken = ref('');
	const barCodeKey = ref('');

	function setAuth(authUser: User) {
		isAuthenticated.value = true;
		user.value = authUser;
		errors.value = '';
		JwtService.saveToken(
			user.value.api_token,
			user.value.api_refresh_token,
			user.value.id
		);
	}

	function setError(error: any) {
		errors.value = error;
	}

	function purgeAuth() {
		isAuthenticated.value = false;
		user.value = {} as User;
		errors.value = '';
		JwtService.destroyToken();
		BaseHandlerService.RemovePermissions();
		localStorage.removeItem(ID_USER_EMAIL_KEY);
		localStorage.removeItem(ID_USER_NAME_KEY);
		localStorage.removeItem(ID_ROLE_NAME_KEY);
	}

	function login(credentials: User) {
		return ApiService.post('Authentication/Login', credentials)
			.then((response: BaseResponse<LoginResponse>) => {
				isFirstLogin.value = response.data.isFirstLogin;

				if (response.data.isFirstLogin) {
					return;
				} else if (!response.data.twoFactorEnabled) {
					sharedLoginProcesses({
						roleName: response.data.roleName,
						userEmail: credentials.email,
						userName: response.data.userName,
					});

					isTwoFactorEnabled.value = false;
					barCodeToken.value = '';
					barCodeKey.value = '';
					const authUser = {
						id: response.data.id,
						api_token: response.data.token,
						api_refresh_token: response.data.refreshToken,
					} as User;
					setAuth(authUser);
					BaseHandlerService.UpdatePermissions(response.data.permissions);
				} else {
					user.value = {
						email: credentials.email,
						password: credentials.password,
					} as User;

					isTwoFactorEnabled.value = response.data.twoFactorEnabled;
					barCodeToken.value = response.data.barCodeToken;
					barCodeKey.value = response.data.barCodeKey;
				}
			})
			.catch(({ response }) => {
				if (response != undefined) setError(response.data.errors);
			});
	}

	function login2FA(credentials: twoFactorRequest) {
		return ApiService.post('Authentication/Login2FA', credentials)
			.then((response: BaseResponse<LoginResponse>) => {
				sharedLoginProcesses({
					roleName: response.data.roleName,
					userEmail: credentials.email,
					userName: response.data.userName,
				});

				const authUser = {
					id: response.data.id,
					api_token: response.data.token,
					api_refresh_token: response.data.refreshToken,
				} as User;
				setAuth(authUser);
				BaseHandlerService.UpdatePermissions(response.data.permissions);
			})
			.catch(({ response }) => {
				setError(response.data.errors);
			});
	}

	function sharedLoginProcesses(request: SharedLoginProcessRequest) {
		window.localStorage.setItem(ID_USER_EMAIL_KEY, request.userEmail);
		window.localStorage.setItem(ID_USER_NAME_KEY, request.userName);
		window.localStorage.setItem(ID_ROLE_NAME_KEY, request.roleName);
	}

	function logout() {
		purgeAuth();
	}

	function register(credentials: User) {
		return ApiService.post('register', credentials)
			.then(({ data }) => {
				setAuth(data);
			})
			.catch(({ response }) => {
				setError(response.data.errors);
			});
	}

	function forgotPassword(email: string) {
		return ApiService.post('forgot_password', email)
			.then(() => {
				setError({});
			})
			.catch(({ response }) => {
				setError(response.data.errors);
			});
	}

	function verifyAuth() {
		if (JwtService.getToken()) {
			ApiService.setHeader();
			ApiService.post('verify_token', {
				api_token: JwtService.getToken(),
			})
				.then(({ data }) => {
					setAuth(data);
				})
				.catch(({ response }) => {
					setError(response.data.errors);
					purgeAuth();
				});
		} else {
			purgeAuth();
		}
	}

	return {
		errors,
		user,
		isAuthenticated,
		isTwoFactorEnabled,
		isFirstLogin,
		barCodeToken,
		barCodeKey,
		purgeAuth,
		login,
		login2FA,
		logout,
		register,
		forgotPassword,
		verifyAuth,
	};
});
